// ============================================================
// parts - one long text stored as several documents.
//
// Firestore refuses a document over 1 MiB, and the SDK retries the refused
// write for ever with every later write queued behind it (v8.310.19). So
// anything that can grow past that - a saved map, a scene's terrain, an
// asset's version - is split into parts under `PART_CHARS` and joined on
// read. PocketBase has no ceiling of this kind; the same parts work there.
//
// Pure. The callers name the documents and decide what the text is.
// ============================================================

/** Characters per part. Under Firestore's 1 MiB with room for the other fields and for UTF-8. */
export const PART_CHARS = 600_000;

/** What a stored part carries. Absent counts mean one part (PocketBase dropped them before v8.334.1). */
export interface Part {
  /** How many parts the whole is in. */
  parts?: number;
  /** This part's index. */
  part?: number;
  /** A slice of the whole text. */
  data: string;
}

/** The slices of `text`, in order: always at least one, so an empty text is still stored. */
export function splitText(text: string, partChars = PART_CHARS): string[] {
  const count = Math.max(1, Math.ceil(text.length / partChars));
  return Array.from({ length: count }, (_, i) => text.slice(i * partChars, (i + 1) * partChars));
}

/**
 * The whole text from its parts, in any arrival order, or null when a part
 * is missing or the counts disagree - a view taken mid-write is torn, and a
 * caller shows nothing rather than half.
 */
export function joinParts(list: readonly Part[]): string | null {
  const parts = list[0]?.parts ?? 1;
  const ordered = [...list].sort((a, b) => (a.part ?? 0) - (b.part ?? 0));
  const complete = ordered.length === parts && ordered.every((d, i) => (d.part ?? 0) === i && (d.parts ?? 1) === parts);
  return complete ? ordered.map((d) => d.data).join('') : null;
}
